import axios from 'axios';
import { ApiResponse } from '../../../shared/types';

const API_BASE = '/api/v1/moodle';

interface MoodleCourse {
  id: number;
  fullname: string;
  shortname: string;
  idnumber?: string;
}

class MoodleApiService {
  async searchCourses(query: string): Promise<ApiResponse<MoodleCourse[]>> {
    const response = await axios.get(`${API_BASE}/courses/search`, {
      params: { q: query },
    });
    return response.data;
  }

  async getCourseTree(): Promise<ApiResponse<any[]>> {
    const response = await axios.get(`${API_BASE}/course-tree`);
    return response.data;
  }
}

export const moodleApiService = new MoodleApiService();

export class MoodleService {
  /**
   * Creates Moodle users for all students in a given class.
   * @param lopHocId - The ID of the class to create users for.
   * @returns The API response from the backend.
   */
  async createUsersForClass(lopHocId: string): Promise<ApiResponse> {
    const response = await axios.post<ApiResponse>(`${API_BASE}/class/${lopHocId}/create-users`);
    return response.data;
  }

  /**
   * Creates Moodle ThiThu users for all students in a given class.
   * @param lopHocId - The ID of the class to create users for.
   * @returns The API response from the backend.
   */
  async createUsersForClassThiThu(lopHocId: string): Promise<ApiResponse> {
    const response = await axios.post<ApiResponse>(`${API_BASE}/class/${lopHocId}/create-users-thithu`);
    return response.data;
  }

  /**
   * Searches for Moodle users.
   * @param query - The search term.
   * @returns A list of found users.
   */
  async searchUsers(query: string): Promise<ApiResponse<any[]>> {
    const response = await axios.get<ApiResponse<any[]>>(`${API_BASE}/users/search`, {
      params: { query },
    });
    return response.data;
  }

  /**
   * Checks if a student exists in Moodle.
   * @param maHS - The student's ID.
   * @returns The Moodle user data if found.
   */
  async checkStudentExists(maHS: string): Promise<ApiResponse> {
    const response = await axios.get<ApiResponse>(`${API_BASE}/student/${maHS}/check`);
    return response.data;
  }

  /**
   * Updates a Moodle user's password.
   * @param userId - The Moodle user ID.
   * @param password - The new password.
   */
  async updateUserPassword(userId: number, password: string): Promise<ApiResponse> {
    const response = await axios.put<ApiResponse>(`${API_BASE}/user/${userId}/password`, { password });
    return response.data;
  }

  /**
   * Creates a single Moodle user.
   * @param userId - The system user ID.
   * @param fullName - The user's full name.
   */
  async createSingleUser(userId: number, fullName: string): Promise<ApiResponse> {
    const response = await axios.post<ApiResponse>(`${API_BASE}/create-user`, { userId, fullName });
    return response.data;
  }
}

export const moodleService = new MoodleService();

export const getCourseTree = async () => {
  const response = await moodleApiService.getCourseTree();
  return response;
};

/**
 * Gets all enrolled users in a Moodle course
 */
export const getEnrolledUsers = async (courseId: number) => {
  const response = await axios.get(`${API_BASE}/courses/${courseId}/enrolled-users`);
  return response.data.data || []; // Extract data from response wrapper
};

/**
 * Unenrols a user from a Moodle course
 */
export const unenrolUser = async (courseId: number, userId: number) => {
  const response = await axios.delete(`${API_BASE}/courses/${courseId}/user/${userId}/unenrol`);
  return response.data;
};

/**
 * Updates user enrollment status (suspend/activate)
 */
export const updateUserEnrolmentStatus = async (courseId: number, userId: number, status: number) => {
  const response = await axios.put(`${API_BASE}/courses/${courseId}/user/${userId}/status`, { status });
  return response.data;
};

export default {
  searchCourses: moodleApiService.searchCourses.bind(moodleApiService),
  createSingleUser: moodleService.createSingleUser.bind(moodleService),
  createUsersForClass: moodleService.createUsersForClass.bind(moodleService),
  searchUsers: moodleService.searchUsers.bind(moodleService),
  checkStudent: moodleService.checkStudentExists.bind(moodleService),
  updateUserPassword: moodleService.updateUserPassword.bind(moodleService),
  getCourseTree,
  getEnrolledUsers,
  unenrolUser,
  updateUserEnrolmentStatus,
}; 